// ============================================================
// TMR Portal Admin — o contrato emitido: evidências, linha do tempo e o PDF
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { Drawer, Field } from '../../components/ui';
import { Notice } from '../../components/Notice';
import { ErrorState, Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { PERMISSIONS, usePermission } from '../../data/PermissionsProvider';
import {
  patientContractFileUrl,
  patientContractSignatureUrl,
  usePatientContract,
  usePatientContractDocument,
  useReprocessPatientContract,
} from '../../data/patientContracts/queries';
import {
  actorLabel,
  canReprocess,
  contractStatusLabel,
  emailStateLabel,
  eventTitle,
  eventsNewestFirst,
  formatEvidenceJson,
  formatIdentityDocument,
  formatInstant,
  formatPatientDocument,
  integrityWarning,
  pdfStateLabel,
  signatoryState,
  signatureMatchesDocument,
  statusNotice,
  verificationCode,
} from '../../data/patientContracts/status';
import type {
  ContractSignatory,
  ContractSignature,
  PatientContractDetail,
  PatientContractRow,
} from '../../data/patientContracts/contract';

/** Props for {@link PatientContractDetailPanel}. */
interface PatientContractDetailPanelProps {
  documentId: string;
  onClose: () => void;
}

/**
 * Everything known about one issued contract.
 *
 * **The integrity block is the reason this panel is not just a record viewer.** A signed contract is
 * evidence, and evidence is worth what its verification is worth: the V2 recomputes the event chain and
 * compares what the patient's browser confirmed at signing against the hash of the text that is stored. A
 * record that holds together says nothing — a green badge on every contract would turn the check into
 * decoration, and the one that is not fine would stop standing out.
 *
 * Reprocessing asks the v1.1 routine to try again; the V2 does not generate the PDF or send the e-mail.
 * Its outcome arrives later as the legacy's own events on this contract's chain (`24` §3.3).
 *
 * The panel reads itself again while the patient has not signed or the PDF is being produced, so the
 * operator waiting on either sees it happen without closing and reopening it (`detailRefreshInterval`).
 */
export function PatientContractDetailPanel({ documentId, onClose }: PatientContractDetailPanelProps) {
  const [reading, setReading] = useState(false);

  const canManage = usePermission(PERMISSIONS.patientContractsManage);

  const query = usePatientContract(documentId);
  const reprocess = useReprocessPatientContract();

  return (
    <Drawer open title="Contrato de paciente" onClose={onClose} width={720}>
      {query.isPending ? (
        <Skeleton rows={8} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <Body
          detail={query.data}
          canManage={canManage}
          reading={reading}
          onRead={() => setReading(true)}
          reprocessing={reprocess.isPending}
          reprocessError={reprocess.error}
          reprocessed={reprocess.isSuccess}
          onReprocess={() => void reprocess.mutateAsync(documentId).catch(() => undefined)}
        />
      )}
    </Drawer>
  );
}

function Body({
  detail,
  canManage,
  reading,
  onRead,
  reprocessing,
  reprocessError,
  reprocessed,
  onReprocess,
}: {
  detail: PatientContractDetail;
  canManage: boolean;
  reading: boolean;
  onRead: () => void;
  reprocessing: boolean;
  reprocessError: unknown;
  reprocessed: boolean;
  onReprocess: () => void;
}) {
  const contract = detail.contract;
  const warning = integrityWarning(detail.integrity);
  const notice = statusNotice(detail);
  const email = emailStateLabel(detail);
  const closed = contract.status === 'Cancelado' || contract.status === 'Expirado';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {warning && <Notice tone="danger">{warning}</Notice>}

      {notice && (
        <Notice tone={notice.tone}>
          <strong>{notice.title}.</strong> {notice.text}
        </Notice>
      )}

      {reprocessed && !reprocessing && (
        <Notice tone="success">
          Reprocessamento pedido. O sistema de vendas tenta de novo no próximo ciclo — o resultado aparece
          na linha do tempo abaixo.
        </Notice>
      )}

      {reprocessError != null && (
        <Notice tone="danger">
          {reprocessError instanceof ApiError
            ? reprocessError.detail
            : 'Não foi possível pedir o reprocessamento.'}
        </Notice>
      )}

      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <Field label="Número">{contract.number}</Field>
        <Field label="Situação">{contractStatusLabel(contract.status, contract.hasPdf)}</Field>
        {/* The code printed on the signed PDF — what somebody holding the paper reads out on the phone. */}
        <Field label="Código de verificação">
          <span style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
            {verificationCode(detail.contentHash)}
          </span>
        </Field>
        <Field label="Paciente">{contract.patientName ?? '—'}</Field>
        <Field label="CPF">{formatPatientDocument(contract.patientDocument)}</Field>
        <Field label="Clínica">
          {contract.companyName ?? `Produtor ${contract.legacyProducerId}`}
        </Field>
        <Field label="Documento">{contract.documentTypeName}</Field>
        <Field label="Emitido em">{formatInstant(contract.issuedAt)}</Field>
        <Field label="Emitido por">{actorLabel(detail.issuedBy)}</Field>
        {contract.status === 'AguardandoAssinatura' && (
          <Field label="Prazo para assinar">{formatInstant(detail.expiresAt)}</Field>
        )}
        <Field label="Assinado em">{formatInstant(contract.signedAt)}</Field>
        {closed && (
          <Field label={contract.status === 'Cancelado' ? 'Cancelado em' : 'Expirou em'}>
            {formatInstant(detail.closedAt)}
          </Field>
        )}
        <Field label="PDF assinado">{pdfStateLabel(detail)}</Field>
        {email && <Field label="Via assinada por e-mail">{email}</Field>}
        {detail.signingPlace && <Field label="Local da assinatura">{detail.signingPlace}</Field>}
        <Field label="Origem">{detail.reference}</Field>
      </section>

      <section style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {contract.hasPdf ? (
          // A plain link: the endpoint answers with `attachment` and the session cookie is same-origin,
          // so the browser downloads it. Never a storage URL and never a SAS (`24` §13).
          <a href={patientContractFileUrl(contract.id)} download className="pa-btn pa-btn-secondary">
            Baixar PDF assinado
          </a>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--fg-muted)', alignSelf: 'center' }}>
            O PDF ainda não foi gerado.
          </span>
        )}

        {!reading && (
          <Btn variant="secondary" onClick={onRead}>
            Ver o texto assinado
          </Btn>
        )}

        {canManage && canReprocess(detail) && (
          <Btn variant="secondary" icon={Icons.swap} disabled={reprocessing} onClick={onReprocess}>
            {reprocessing ? 'Pedindo…' : 'Reprocessar'}
          </Btn>
        )}
      </section>

      {detail.lastProcessingError && (
        <Notice tone="warning">
          <strong>Última falha no processamento:</strong> {detail.lastProcessingError}
          {detail.processingAttempts > 0 && ` (${detail.processingAttempts} tentativa${detail.processingAttempts === 1 ? '' : 's'})`}
        </Notice>
      )}

      {reading && <SignedText documentId={contract.id} />}

      <Signatories detail={detail} />

      <Timeline detail={detail} />
    </div>
  );
}

/** The frozen text, fetched only when somebody asks to read it. */
function SignedText({ documentId }: { documentId: string }) {
  const query = usePatientContractDocument(documentId);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionTitle>Texto assinado</SectionTitle>

      {query.isPending ? (
        <Skeleton rows={5} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          {!query.data.hashMatchesContent && (
            <Notice tone="danger">
              O texto guardado não confere com o hash gravado junto dele. O registro foi alterado depois da
              emissão.
            </Notice>
          )}
          {/* Sanitized by the backend, where the Markdown is parsed — so this is exactly what the issuing
              engine produced. */}
          <div
            className="pa-prose"
            style={{
              fontSize: 13.5,
              lineHeight: 1.7,
              maxHeight: 420,
              overflowY: 'auto',
              padding: 12,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
            }}
            dangerouslySetInnerHTML={{ __html: query.data.html }}
          />
          <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
            SHA-256 {query.data.contentHash}
          </span>
        </>
      )}
    </section>
  );
}

/** Who signs, and the evidence captured for those who did. */
function Signatories({ detail }: { detail: PatientContractDetail }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SectionTitle>Signatários</SectionTitle>

      {detail.signatories.map((signatory) => (
        <SignatoryCard
          key={signatory.id}
          signatory={signatory}
          signature={detail.signatures.find((item) => item.signatoryId === signatory.id) ?? null}
          contract={detail.contract}
          contentHash={detail.contentHash}
        />
      ))}
    </section>
  );
}

function SignatoryCard({
  signatory,
  signature,
  contract,
  contentHash,
}: {
  signatory: ContractSignatory;
  signature: ContractSignature | null;
  contract: PatientContractRow;
  contentHash: string;
}) {
  const [showEvidence, setShowEvidence] = useState(false);
  // The issuer agrees by issuing and never draws a signature; reading it as "not signed" would flag a
  // contract as missing something it never needed.
  const state = signatoryState(signatory, contract);

  return (
    <div className="pa-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600 }}>{signatory.name}</span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>
            {signatory.role} · {formatIdentityDocument(signatory.identityDocument)}
          </span>
        </div>
        <span style={{ fontSize: 13 }}>{state.label}</span>
      </div>

      {signature ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
            <Field label="Assinado em">{formatInstant(signature.signedAt)}</Field>
            <Field label="IP">{signature.ip ?? '—'}</Field>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Aceite registrado</span>
            <span style={{ fontSize: 13.5, fontStyle: 'italic' }}>“{signature.acceptanceText}”</span>
          </div>

          <img
            src={patientContractSignatureUrl(signatory.id)}
            alt={`Assinatura de ${signatory.name}`}
            style={{
              maxWidth: 320,
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              background: '#fff',
            }}
          />

          {!signatureMatchesDocument(signature, contentHash) && (
            <Notice tone="danger">
              O hash que o navegador confirmou no momento da assinatura não é o do texto guardado.
            </Notice>
          )}

          <button
            type="button"
            className="pa-link-btn"
            onClick={() => setShowEvidence((open) => !open)}
            style={{ alignSelf: 'flex-start' }}
          >
            {showEvidence ? 'Ocultar evidências técnicas' : 'Ver evidências técnicas'}
          </button>

          {showEvidence && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Evidence label="Aparelho" json={signature.deviceJson} />
              <Evidence label="Localização" json={signature.locationJson} />
              <Evidence label="Autenticação" json={signature.authenticationJson} />
              {signature.userAgent && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-subtle)' }}>Navegador</span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                    {signature.userAgent}
                  </span>
                </div>
              )}
              <span style={{ fontSize: 12, color: 'var(--fg-subtle)', fontFamily: 'var(--font-mono)' }}>
                Hash das evidências {signature.evidenceHash}
              </span>
            </div>
          )}
        </>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--fg-muted)' }}>{state.detail}</span>
      )}
    </div>
  );
}

/** One blob of captured evidence, as the legacy stored it. */
function Evidence({ label, json }: { label: string; json: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-subtle)' }}>{label}</span>
      <pre
        style={{
          margin: 0,
          padding: 10,
          background: 'var(--cream-100)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          lineHeight: 1.5,
          overflowX: 'auto',
        }}
      >
        {formatEvidenceJson(json)}
      </pre>
    </div>
  );
}

/**
 * The contract's own chained timeline, as the legacy recorded it — newest first, in the operator's words.
 * The chain is verified in sequence order by the backend; the order here is only for reading.
 */
function Timeline({ detail }: { detail: PatientContractDetail }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionTitle>Linha do tempo do documento</SectionTitle>

      {eventsNewestFirst(detail.events).map((event) => (
        <div
          key={event.sequence}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            paddingBottom: 8,
            borderBottom: '1px solid var(--border-default)',
            // The step where the chain stops verifying, marked where somebody reading the history looks.
            background:
              detail.integrity.chainBrokenAtSequence === event.sequence ? 'oklch(96% 0.03 28)' : undefined,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {event.sequence}. {eventTitle(event.type)}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>{event.description}</span>
          <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
            {formatInstant(event.occurredAt)} · {actorLabel(event.actor)}
            {event.ip ? ` · ${event.ip}` : ''}
          </span>
        </div>
      ))}
    </section>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--fg-subtle)',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </span>
  );
}
